import ExcludedCategoryCard from '@app/components/excluded-items/excluded-category-card';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import {
  ExcludedCategorySummary,
  useGetExcludedSoldItemsSummary,
} from '@shared/api/hooks/sold-goods/excluded-items';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { generatePath } from 'react-router-dom';
import { soldGoodsRoutes } from '../../sold-goods-routes';
import { excludedItemsConfig } from '../tabs';

const INTRO_PARAGRAPHS = [
  'Your sales reports will only include items that we can confidently model as either food or packaging. Non food or packaging items are intentionally excluded, along with items that could not be identified or that were missing appropriate weight information.',
  'To ensure as much of your sales data as possible is assessed, we recommend reviewing these items and including additional information where possible.',
];

/** A right-aligned label + big value. */
function PageStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <Text variant="unit">{label}</Text>
      <Text className="text-4xl font-medium text-primary-800">{value}</Text>
    </div>
  );
}

/**
 * Landing page for the sold-goods Excluded items hub — the sold-goods sibling of
 * the purchased landing page. Splits excluded items into "Items for review"
 * (uncertain) and "Other goods", each linking to its own list. Spend isn't
 * supported for sold goods, so only item counts are shown.
 */
export default function ExcludedSoldItemsLandingPage() {
  const { data: summary } = useGetExcludedSoldItemsSummary();

  const cards: {
    key: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    buttonLabel: string;
    to: string;
    stats: ExcludedCategorySummary;
    primary?: boolean;
  }[] = summary
    ? [
        {
          key: 'uncertain',
          icon: <Icons.help className="size-5" />,
          title: excludedItemsConfig.uncertain.title,
          description: excludedItemsConfig.uncertain.description,
          buttonLabel: 'Review items',
          to: generatePath(soldGoodsRoutes.SOLD_EXCLUDED_ITEMS_UNCERTAIN),
          stats: summary.uncertain,
          primary: true,
        },
        {
          key: 'other',
          icon: <Icons.cookingPot className="size-5" />,
          title: excludedItemsConfig.other.title,
          description: excludedItemsConfig.other.description,
          buttonLabel: 'View items',
          to: generatePath(soldGoodsRoutes.SOLD_EXCLUDED_ITEMS_OTHER),
          stats: summary.other,
        },
      ]
    : [];

  return (
    <Page name="Excluded items" title="Excluded items">
      <PageHeader
        name="Excluded items"
        breadcrumbs={[
          {
            label: 'Sold goods',
            url: generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK),
          },
          {
            label: 'Excluded items',
            url: generatePath(soldGoodsRoutes.SOLD_EXCLUDED_ITEMS),
          },
        ]}
      />

      {summary && (
        <div className="max-w-5xl">
          <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="flex max-w-xl flex-col gap-4">
              {INTRO_PARAGRAPHS.map((paragraph) => (
                <Text key={paragraph} variant="subtle">
                  {paragraph}
                </Text>
              ))}
            </div>
            {/* pr-6 matches the cards' p-6 so the header total aligns with the
                per-card counts below. */}
            <div className="flex shrink-0 gap-12 pr-6">
              <PageStat
                label="Excluded items"
                value={summary.total.count.toLocaleString('en-GB')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {cards.map((card) => (
              <ExcludedCategoryCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                description={card.description}
                count={card.stats.count}
                buttonLabel={card.buttonLabel}
                to={card.to}
                primary={card.primary}
              />
            ))}
          </div>
        </div>
      )}
    </Page>
  );
}
